/**
 * Window Shell JavaScript
 * Handles window interactions: dragging, resizing, controls
 */

// Prevent redeclaration if script is loaded multiple times
if (typeof WindowShell === 'undefined') {
    class WindowShell {
        constructor(windowElement) {
            this.window = windowElement;
            this.windowId = windowElement.dataset.windowId;
            this.isMaximized = false;
            this.isMinimized = false;
            this.previousState = null;
            this.isDragging = false;
            this.isResizing = false;
            this.dragStart = { x: 0, y: 0 };
            this.windowStart = { x: 0, y: 0, width: 0, height: 0 };
            this.resizeDirection = null;

            this.init();
        }

        init() {
            this.setupControls();
            this.setupDragging();
            this.setupResizing();
            this.setupKeyboard();
        }

        /**
         * Setup window control buttons
         */
        setupControls() {
            // Only handle actual window control buttons (minimize, maximize, close)
            // Ignore other data-action elements like buttons with data-action="open-window"
            const controlActions = ['minimize', 'maximize', 'close'];
            const controls = this.window.querySelectorAll('[data-action]');

            controls.forEach(control => {
                const action = control.dataset.action;

                // Only handle actual window control actions
                if (!controlActions.includes(action)) {
                    return; // Skip this control, let other handlers process it
                }

                // Use capture phase to ensure we handle it before other handlers (like window focus)
                control.addEventListener('click', (e) => {
                    console.log('[WindowShell] Control button clicked:', action, 'Window ID:', this.windowId);
                    console.log('[WindowShell] Event target:', e.target);
                    console.log('[WindowShell] Event currentTarget:', e.currentTarget);

                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();

                    // For close button, ensure window is removed from manager immediately
                    if (action === 'close') {
                        console.log('[WindowShell] Close button clicked, calling close()');
                        // Call close immediately without any delay
                        this.close();
                    } else {
                        this.handleControlAction(action);
                    }
                }, true); // Use capture phase to handle before bubbling
            });
        }

        /**
         * Handle control button actions
         */
        handleControlAction(action) {
            switch (action) {
                case 'minimize':
                    this.minimize();
                    break;
                case 'maximize':
                    if (this.isMaximized) {
                        this.restore();
                    } else {
                        this.maximize();
                    }
                    break;
                case 'close':
                    this.close();
                    break;
            }
        }

        /**
         * Minimize window
         */
        minimize() {
            if (this.isMinimized) return;

            this.isMinimized = true;
            this.window.classList.add('st-window--minimizing');

            setTimeout(() => {
                this.window.classList.add('st-window--minimized');
                this.window.classList.remove('st-window--minimizing');
            }, 150);

            // Update taskbar
            if (window.smashOS && window.smashOS.desktopManager) {
                window.smashOS.desktopManager.updateWindowTaskbarState(this.windowId, false);
            }

            // Trigger event
            this.window.dispatchEvent(new CustomEvent('window:minimize', {
                detail: { windowId: this.windowId }
            }));
        }

        /**
         * Maximize window
         */
        maximize() {
            if (this.isMaximized || !this.canMaximize()) return;

            // Store previous state
            this.previousState = {
                x: this.window.style.left,
                y: this.window.style.top,
                width: this.window.style.width,
                height: this.window.style.height
            };

            this.isMaximized = true;
            this.window.classList.add('st-window--maximized');

            // Update maximize button
            const maxBtn = this.window.querySelector('[data-action="maximize"]');
            if (maxBtn) {
                maxBtn.querySelector('span').textContent = '❐'; // Restore icon
                maxBtn.setAttribute('aria-label', maxBtn.getAttribute('aria-label').replace('Maximize', 'Restore'));
            }

            // Trigger event
            this.window.dispatchEvent(new CustomEvent('window:maximize', {
                detail: { windowId: this.windowId }
            }));
        }

        /**
         * Restore window from maximized or minimized state
         */
        restore() {
            // Handle minimized state
            if (this.isMinimized) {
                this.isMinimized = false;
                this.window.classList.remove('st-window--minimized', 'st-window--minimizing');

                // Update taskbar
                if (window.smashOS && window.smashOS.desktopManager) {
                    window.smashOS.desktopManager.updateWindowTaskbarState(this.windowId, true);
                }

                // Trigger event
                this.window.dispatchEvent(new CustomEvent('window:restore', {
                    detail: { windowId: this.windowId }
                }));

                return;
            }

            // Handle maximized state
            if (!this.isMaximized) return;

            this.isMaximized = false;
            this.window.classList.remove('st-window--maximized');

            // Restore previous state
            if (this.previousState) {
                this.window.style.left = this.previousState.x;
                this.window.style.top = this.previousState.y;
                this.window.style.width = this.previousState.width;
                this.window.style.height = this.previousState.height;
            }

            // Update maximize button
            const maxBtn = this.window.querySelector('[data-action="maximize"]');
            if (maxBtn) {
                maxBtn.querySelector('span').textContent = '□'; // Maximize icon
                maxBtn.setAttribute('aria-label', maxBtn.getAttribute('aria-label').replace('Restore', 'Maximize'));
            }

            // Trigger event
            this.window.dispatchEvent(new CustomEvent('window:restore', {
                detail: { windowId: this.windowId }
            }));
        }

        /**
         * Close window
         */
        close() {
            console.log('[WindowShell] close() called for window:', this.windowId);
            // Prevent window from being brought to front during close
            this.window.style.pointerEvents = 'none';
            this.window.classList.add('st-window--closing');
            console.log('[WindowShell] Window marked as closing, pointer events disabled');

            // Remove from window manager - it will handle DOM removal
            if (window.smashOS && window.smashOS.windowManager) {
                console.log('[WindowShell] Calling windowManager.close()');
                window.smashOS.windowManager.close(this.windowId);
                console.log('[WindowShell] windowManager.close() completed');
            } else {
                console.warn('[WindowShell] windowManager not available!');
                // Fallback: remove from DOM ourselves if windowManager not available
                setTimeout(() => {
                    if (this.window && this.window.parentElement) {
                        this.window.remove();
                    }
                }, 300);
            }

            // Trigger event
            this.window.dispatchEvent(new CustomEvent('window:close', {
                detail: { windowId: this.windowId }
            }));
        }

        /**
         * Check if window can be maximized
         */
        canMaximize() {
            return this.window.dataset.noMaximize !== 'true';
        }

        /**
         * Setup window dragging
         */
        setupDragging() {
            const titlebar = this.window.querySelector('[data-draggable="true"]');
            if (!titlebar) return;

            // Use Pointer Events API if available (better cross-device support)
            if (window.PointerEvent) {
                titlebar.addEventListener('pointerdown', (e) => {
                    // Only allow dragging with primary button (left click)
                    // button === 0: primary button, button === 2: secondary button (right click)
                    if (e.button !== 0) return;
                    if (this.isControlButton(e.target)) return;
                    // Raise window on any pointer-down (including mobile tap)
                    this.bringToFront();
                    // Guard: on coarse-pointer devices, don't start a drag
                    if (window.matchMedia('(pointer: coarse)').matches) return;
                    if (this.isMaximized) return;
                    e.preventDefault();
                    titlebar.setPointerCapture(e.pointerId);
                    this.startDrag(e);
                });
            } else {
                // Fallback to mouse/touch events
                titlebar.addEventListener('mousedown', (e) => {
                    // Only allow dragging with primary button (left click)
                    // button === 0: primary button, button === 2: secondary button (right click)
                    if (e.button !== 0) return;
                    if (this.isControlButton(e.target)) return;
                    // Raise window on any mouse-down
                    this.bringToFront();
                    // Guard: on coarse-pointer devices, don't start a drag
                    if (window.matchMedia('(pointer: coarse)').matches) return;
                    if (this.isMaximized) return;
                    this.startDrag(e);
                });

                titlebar.addEventListener('touchstart', (e) => {
                    // Touch events don't have button property, so always allow
                    if (this.isControlButton(e.target)) return;
                    // Raise window on touch
                    this.bringToFront();
                    // Guard: on coarse-pointer devices, don't start a drag
                    if (window.matchMedia('(pointer: coarse)').matches) return;
                    if (this.isMaximized) return;
                    e.preventDefault(); // Prevent scrolling when dragging
                    e.stopPropagation();
                    this.startDrag(e.touches[0]);
                }, { passive: false });
            }
        }

        /**
         * Check if target is a control button
         */
        isControlButton(target) {
            // Check if target is inside the controls area or is a control button
            const controls = target.closest('.st-titlebar__controls');
            if (controls) return true;

            // Check if target has data-action attribute
            if (target.closest('[data-action]')) return true;

            // Check if target is the button itself or its child
            if (target.matches('[data-action]') || target.closest('button[data-action]')) return true;

            return false;
        }

        /**
         * Start dragging
         */
        startDrag(e) {
            // Prevent rapid clicks from starting drag
            if (this.dragStartTime && Date.now() - this.dragStartTime < 100) {
                return;
            }
            this.dragStartTime = Date.now();

            this.isDragging = true;
            this.dragStart.x = e.clientX || (e.touches && e.touches[0].clientX);
            this.dragStart.y = e.clientY || (e.touches && e.touches[0].clientY);
            this.pointerId = e.pointerId;

            // Check if window is centered and convert to absolute positioning
            const isCentered = this.window.dataset.centered === 'true' ||
                (this.window.style.transform && this.window.style.transform.includes('translate(-50%, -50%)'));

            if (isCentered) {
                // Remove centering transform and convert to absolute positioning
                const rect = this.window.getBoundingClientRect();
                this.window.style.position = 'absolute';
                this.window.style.left = `${rect.left}px`;
                this.window.style.top = `${rect.top}px`;
                this.window.style.transform = 'none';
                this.window.style.right = 'auto';
                this.window.style.bottom = 'auto';
                this.window.dataset.centered = 'false';

                // Update window manager
                if (window.smashOS && window.smashOS.windowManager) {
                    const windowData = window.smashOS.windowManager.windows.get(this.windowId);
                    if (windowData) {
                        windowData.position = { x: rect.left, y: rect.top, centered: false };
                    }
                }

                this.windowStart.x = rect.left;
                this.windowStart.y = rect.top;
            } else {
                const rect = this.window.getBoundingClientRect();
                this.windowStart.x = rect.left;
                this.windowStart.y = rect.top;
            }

            // Add visual feedback
            this.window.classList.add('st-window--dragging');
            document.body.style.cursor = 'move';
            document.body.style.userSelect = 'none';

            // Bring to front
            this.bringToFront();

            // Use Pointer Events if available
            if (window.PointerEvent && this.pointerId !== undefined) {
                const titlebar = this.window.querySelector('[data-draggable="true"]');
                if (titlebar) {
                    titlebar.addEventListener('pointermove', this.handleDragMove);
                    titlebar.addEventListener('pointerup', this.handleDragEnd);
                    titlebar.addEventListener('pointercancel', this.handleDragEnd);
                }
            } else {
                // Fallback to mouse/touch events
                document.addEventListener('mousemove', this.handleDragMove);
                document.addEventListener('mouseup', this.handleDragEnd);
                document.addEventListener('touchmove', this.handleDragMove, { passive: false });
                document.addEventListener('touchend', this.handleDragEnd);
                document.addEventListener('touchcancel', this.handleDragEnd);
            }
        }

        /**
         * Handle drag move
         */
        handleDragMove = (e) => {
            if (!this.isDragging) return;

            // Prevent default to avoid text selection and scrolling
            e.preventDefault();

            // Handle Pointer Events
            if (e.pointerId !== undefined && e.pointerId !== this.pointerId) {
                return; // Ignore other pointers
            }

            const clientX = e.clientX || (e.touches && e.touches[0] && e.touches[0].clientX) || e.pageX;
            const clientY = e.clientY || (e.touches && e.touches[0] && e.touches[0].clientY) || e.pageY;

            if (clientX === undefined || clientY === undefined) return;

            const deltaX = clientX - this.dragStart.x;
            const deltaY = clientY - this.dragStart.y;

            // Only start dragging after a small threshold to prevent accidental drags
            if (!this.hasDragged && (Math.abs(deltaX) < 3 && Math.abs(deltaY) < 3)) {
                return;
            }
            this.hasDragged = true;

            let newX = this.windowStart.x + deltaX;
            let newY = this.windowStart.y + deltaY;

            // Clamp to viewport with minimum margins
            const desktop = document.getElementById('desktop');
            const desktopRect = desktop ? desktop.getBoundingClientRect() : { width: window.innerWidth, height: window.innerHeight };
            const taskbarHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--st-taskbar-height')) || 32;
            const isMobile = window.innerWidth <= 767;
            const minMargin = isMobile ? 16 : 20; // Minimum spacing from taskbar
            // Check if this is a hero banner window (special top margin on mobile)
            const windowId = this.window.id.replace('window-', '');
            const isHeroBanner = windowId.includes('hero-banner') || this.window.getAttribute('data-section-name') === 'window-hero-banner';
            const minMarginTop = (isMobile && isHeroBanner) ? 200 : (isMobile ? 16 : 20); // 200px for hero banner on mobile, 16px for others on mobile, 20px on desktop
            const maxBottom = desktopRect.height - taskbarHeight - minMargin; // Maximum bottom position

            const windowWidth = this.window.offsetWidth;
            const windowHeight = this.window.offsetHeight;

            // Constrain to viewport with minimum margins (prevent horizontal/vertical overflow)
            const maxX = Math.max(minMargin, desktopRect.width - windowWidth);
            const maxY = Math.max(minMarginTop, desktopRect.height - taskbarHeight - windowHeight - minMargin);

            newX = Math.max(minMargin, Math.min(newX, maxX));
            newY = Math.max(minMargin, Math.min(newY, maxY));

            // Ensure window doesn't overlap taskbar (bottom constraint)
            const windowBottom = newY + windowHeight;
            if (windowBottom > maxBottom) {
                newY = Math.max(minMargin, maxBottom - windowHeight);
            }

            // Remove transform if present (from centering)
            if (this.window.style.transform) {
                this.window.style.transform = 'none';
            }

            // Ensure position is absolute, not fixed
            if (this.window.style.position === 'fixed') {
                this.window.style.position = 'absolute';
            }

            this.window.style.left = `${newX}px`;
            this.window.style.top = `${newY}px`;
            this.window.style.right = 'auto';
            this.window.style.bottom = 'auto';

            // Mark as no longer centered
            this.window.dataset.centered = 'false';

            // Update window manager position tracking
            if (window.smashOS && window.smashOS.windowManager) {
                window.smashOS.windowManager.updatePosition(this.windowId, { x: newX, y: newY });

                // Update window data to remove centered flag
                const windowData = window.smashOS.windowManager.windows.get(this.windowId);
                if (windowData) {
                    windowData.position = { x: newX, y: newY, centered: false };
                }
            }
        };

        /**
         * Handle drag end
         */
        handleDragEnd = (e) => {
            if (!this.isDragging) return;

            // Handle Pointer Events
            if (window.PointerEvent && this.pointerId !== undefined) {
                const titlebar = this.window.querySelector('[data-draggable="true"]');
                if (titlebar && e && e.pointerId === this.pointerId) {
                    titlebar.releasePointerCapture(e.pointerId);
                    titlebar.removeEventListener('pointermove', this.handleDragMove);
                    titlebar.removeEventListener('pointerup', this.handleDragEnd);
                    titlebar.removeEventListener('pointercancel', this.handleDragEnd);
                }
            } else {
                // Fallback to mouse/touch events
                document.removeEventListener('mousemove', this.handleDragMove);
                document.removeEventListener('mouseup', this.handleDragEnd);
                document.removeEventListener('touchmove', this.handleDragMove);
                document.removeEventListener('touchend', this.handleDragEnd);
                document.removeEventListener('touchcancel', this.handleDragEnd);
            }

            // Remove visual feedback
            this.window.classList.remove('st-window--dragging');
            document.body.style.cursor = '';
            document.body.style.userSelect = '';

            // Reset drag state
            this.isDragging = false;
            this.hasDragged = false;
            this.pointerId = undefined;
            this.dragStartTime = null;
        };

        /**
         * Setup window resizing
         */
        setupResizing() {
            if (this.window.dataset.noResize === 'true') return;
            if (this.isMaximized) return; // Can't resize maximized windows

            const handles = this.window.querySelectorAll('.st-window__resize-handle');
            console.log('[WindowShell] Found resize handles:', handles.length, handles);

            // On mobile, only enable status bar resize handle (larger, easier to use)
            const isMobile = window.innerWidth <= 767;

            handles.forEach(handle => {
                // Skip edge/corner handles on mobile - only allow status bar handle
                if (isMobile && !handle.classList.contains('st-statusbar__resize-handle')) {
                    return;
                }
                // Use Pointer Events API if available (better cross-device support)
                if (window.PointerEvent) {
                    handle.addEventListener('pointerdown', (e) => {
                        // Only allow resizing with primary button (left click)
                        // button === 0: primary button, button === 2: secondary button (right click)
                        if (e.button !== 0) return;
                        if (this.isMaximized) return;
                        // Guard: on coarse-pointer devices, don't start a resize
                        if (window.matchMedia('(pointer: coarse)').matches) return;
                        e.preventDefault();
                        handle.setPointerCapture(e.pointerId);
                        this.startResize(e, handle.dataset.resize);
                    });
                } else {
                    // Fallback to mouse/touch events
                    handle.addEventListener('mousedown', (e) => {
                        // Only allow resizing with primary button (left click)
                        // button === 0: primary button, button === 2: secondary button (right click)
                        if (e.button !== 0) return;
                        if (this.isMaximized) return;
                        // Guard: on coarse-pointer devices, don't start a resize
                        if (window.matchMedia('(pointer: coarse)').matches) return;
                        e.preventDefault();
                        this.startResize(e, handle.dataset.resize);
                    });

                    handle.addEventListener('touchstart', (e) => {
                        // Touch events don't have button property, so always allow
                        if (this.isMaximized) return;
                        // Guard: on coarse-pointer devices, don't start a resize
                        if (window.matchMedia('(pointer: coarse)').matches) return;
                        e.preventDefault();
                        e.stopPropagation();
                        this.startResize(e.touches[0], handle.dataset.resize);
                    }, { passive: false });
                }
            });
        }

        /**
         * Start resizing
         */
        startResize(e, direction) {
            if (this.isMaximized) return; // Can't resize maximized windows

            this.isResizing = true;
            this.resizeDirection = direction;
            this.dragStart.x = e.clientX || (e.touches && e.touches[0].clientX);
            this.dragStart.y = e.clientY || (e.touches && e.touches[0].clientY);
            this.pointerId = e.pointerId;

            const rect = this.window.getBoundingClientRect();
            this.windowStart.x = rect.left;
            this.windowStart.y = rect.top;
            this.windowStart.width = rect.width;
            this.windowStart.height = rect.height;

            // If window height is auto, convert to fixed height based on current content
            if (this.window.style.height === 'auto' || this.window.style.height === '') {
                this.window.style.height = `${rect.height}px`;
                this.windowStart.height = rect.height;
            }

            // Add visual feedback
            this.window.classList.add('st-window--resizing');
            document.body.style.userSelect = 'none';

            // Bring to front during resize
            this.bringToFront();

            // Use Pointer Events if available
            if (window.PointerEvent && this.pointerId !== undefined) {
                const handle = e.target;
                if (handle) {
                    handle.addEventListener('pointermove', this.handleResizeMove);
                    handle.addEventListener('pointerup', this.handleResizeEnd);
                    handle.addEventListener('pointercancel', this.handleResizeEnd);
                }
            } else {
                // Fallback to mouse/touch events
                document.addEventListener('mousemove', this.handleResizeMove);
                document.addEventListener('mouseup', this.handleResizeEnd);
                document.addEventListener('touchmove', this.handleResizeMove, { passive: false });
                document.addEventListener('touchend', this.handleResizeEnd);
                document.addEventListener('touchcancel', this.handleResizeEnd);
            }
        }

        /**
         * Handle resize move
         */
        handleResizeMove = (e) => {
            if (!this.isResizing) return;
            if (this.isMaximized) {
                this.handleResizeEnd(e);
                return;
            }

            // Prevent default to avoid text selection and scrolling
            e.preventDefault();

            // Handle Pointer Events
            if (e.pointerId !== undefined && e.pointerId !== this.pointerId) {
                return; // Ignore other pointers
            }

            const clientX = e.clientX || (e.touches && e.touches[0] && e.touches[0].clientX) || e.pageX;
            const clientY = e.clientY || (e.touches && e.touches[0] && e.touches[0].clientY) || e.pageY;

            if (clientX === undefined || clientY === undefined) return;

            const deltaX = clientX - this.dragStart.x;
            const deltaY = clientY - this.dragStart.y;

            // Get min/max dimensions
            const minWidth = parseInt(this.window.style.minWidth) || parseInt(getComputedStyle(this.window).minWidth) || 320;

            // Calculate minimum height based on window structure (titlebar + statusbar + minimum content)
            const titlebar = this.window.querySelector('.st-window__titlebar');
            const menubar = this.window.querySelector('.st-window__menubar');
            const statusbar = this.window.querySelector('.st-window__statusbar');
            const titlebarHeight = titlebar ? titlebar.offsetHeight : 20;
            const menubarHeight = menubar ? menubar.offsetHeight : 0;
            const statusbarHeight = statusbar ? statusbar.offsetHeight : 20;
            const minContentHeight = 100; // Minimum content height
            const calculatedMinHeight = titlebarHeight + menubarHeight + statusbarHeight + minContentHeight;

            const minHeight = Math.max(
                parseInt(this.window.style.minHeight) || parseInt(getComputedStyle(this.window).minHeight) || calculatedMinHeight,
                calculatedMinHeight
            );
            const maxWidth = parseInt(this.window.style.maxWidth) || parseInt(getComputedStyle(this.window).maxWidth) || Infinity;
            const maxHeight = parseInt(this.window.style.maxHeight) || parseInt(getComputedStyle(this.window).maxHeight) || Infinity;

            const desktop = document.getElementById('desktop');
            const desktopRect = desktop ? desktop.getBoundingClientRect() : { width: window.innerWidth, height: window.innerHeight };
            const taskbarHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--st-taskbar-height')) || 32;
            const isMobile = window.innerWidth <= 767;
            const minMargin = isMobile ? 16 : 20; // Minimum spacing from taskbar
            // Check if this is a hero banner window (special top margin on mobile)
            const windowId = this.window.id.replace('window-', '');
            const isHeroBanner = windowId.includes('hero-banner') || this.window.getAttribute('data-section-name') === 'window-hero-banner';
            const minMarginTop = (isMobile && isHeroBanner) ? 200 : (isMobile ? 16 : 20); // 200px for hero banner on mobile, 16px for others on mobile, 20px on desktop
            const maxBottom = desktopRect.height - taskbarHeight - minMargin; // Maximum bottom position

            // Constrain max dimensions to viewport (prevent overflow)
            const viewportMaxWidth = desktopRect.width;
            const viewportMaxHeight = desktopRect.height - taskbarHeight - minMarginTop - minMargin; // Top and bottom margins
            const effectiveMaxWidth = Math.min(maxWidth, viewportMaxWidth);
            const effectiveMaxHeight = Math.min(maxHeight, viewportMaxHeight);

            let newWidth = this.windowStart.width;
            let newHeight = this.windowStart.height;
            let newX = this.windowStart.x;
            let newY = this.windowStart.y;

            const dir = this.resizeDirection;

            // Calculate new dimensions based on resize direction
            if (dir.includes('e')) {
                // Resize from east (right)
                newWidth = Math.max(minWidth, Math.min(
                    this.windowStart.width + deltaX,
                    Math.min(effectiveMaxWidth, viewportMaxWidth - this.windowStart.x)
                ));
            }
            if (dir.includes('w')) {
                // Resize from west (left)
                const maxWidthChange = Math.min(this.windowStart.width - minWidth, this.windowStart.x);
                const widthChange = Math.max(-maxWidthChange, Math.min(deltaX, this.windowStart.width - minWidth));
                newWidth = this.windowStart.width - widthChange;
                newX = this.windowStart.x + widthChange;
            }
            if (dir.includes('s')) {
                // Resize from south (bottom)
                // Ensure we never go below minimum height that includes status bar
                const statusbar = this.window.querySelector('.st-window__statusbar');
                const statusbarHeight = statusbar ? statusbar.offsetHeight : 20;
                const titlebar = this.window.querySelector('.st-window__titlebar');
                const titlebarHeight = titlebar ? titlebar.offsetHeight : 20;
                const menubar = this.window.querySelector('.st-window__menubar');
                const menubarHeight = menubar ? menubar.offsetHeight : 0;
                // Minimum height must include titlebar, menubar, statusbar, and at least 100px for content
                const absoluteMinHeight = titlebarHeight + menubarHeight + statusbarHeight + 100;
                const effectiveMinHeight = Math.max(minHeight, absoluteMinHeight);

                newHeight = Math.max(effectiveMinHeight, Math.min(
                    this.windowStart.height + deltaY,
                    Math.min(effectiveMaxHeight, viewportMaxHeight - this.windowStart.y)
                ));
            }
            if (dir.includes('n')) {
                // Resize from north (top)
                // Ensure we never go below minimum height that includes status bar
                const statusbar = this.window.querySelector('.st-window__statusbar');
                const statusbarHeight = statusbar ? statusbar.offsetHeight : 20;
                const titlebar = this.window.querySelector('.st-window__titlebar');
                const titlebarHeight = titlebar ? titlebar.offsetHeight : 20;
                const menubar = this.window.querySelector('.st-window__menubar');
                const menubarHeight = menubar ? menubar.offsetHeight : 0;
                // Minimum height must include titlebar, menubar, statusbar, and at least 100px for content
                const absoluteMinHeight = titlebarHeight + menubarHeight + statusbarHeight + 100;
                const effectiveMinHeight = Math.max(minHeight, absoluteMinHeight);

                const maxHeightChange = Math.min(this.windowStart.height - effectiveMinHeight, this.windowStart.y);
                const heightChange = Math.max(-maxHeightChange, Math.min(deltaY, this.windowStart.height - effectiveMinHeight));
                newHeight = this.windowStart.height - heightChange;
                newY = this.windowStart.y + heightChange;
            }

    // Clamp position to viewport with minimum margins (prevent horizontal/vertical overflow)
    const maxX = Math.max(minMargin, viewportMaxWidth - newWidth);
    const maxY = Math.max(minMarginTop, viewportMaxHeight - newHeight);
    newX = Math.max(minMargin, Math.min(newX, maxX));
    newY = Math.max(minMarginTop, Math.min(newY, maxY));

            // Ensure window doesn't exceed viewport dimensions
            newWidth = Math.min(newWidth, viewportMaxWidth);
            newHeight = Math.min(newHeight, viewportMaxHeight);

            // Ensure window doesn't overlap taskbar (bottom constraint)
            const windowBottom = newY + newHeight;
            if (windowBottom > maxBottom) {
                newY = Math.max(minMarginTop, maxBottom - newHeight);
            }

            // Apply new dimensions and position
            this.window.style.width = `${newWidth}px`;
            this.window.style.height = `${newHeight}px`;
            this.window.style.left = `${newX}px`;
            this.window.style.top = `${newY}px`;

            // Update window manager size and position tracking
            if (window.smashOS && window.smashOS.windowManager) {
                window.smashOS.windowManager.updateSize(this.windowId, { width: newWidth, height: newHeight });
                window.smashOS.windowManager.updatePosition(this.windowId, { x: newX, y: newY });
            }
        };

        /**
         * Handle resize end
         */
        handleResizeEnd = (e) => {
            if (!this.isResizing) return;

            // Handle Pointer Events
            if (window.PointerEvent && this.pointerId !== undefined) {
                const handle = e && e.target;
                if (handle && e && e.pointerId === this.pointerId) {
                    handle.releasePointerCapture(e.pointerId);
                    handle.removeEventListener('pointermove', this.handleResizeMove);
                    handle.removeEventListener('pointerup', this.handleResizeEnd);
                    handle.removeEventListener('pointercancel', this.handleResizeEnd);
                }
            } else {
                // Fallback to mouse/touch events
                document.removeEventListener('mousemove', this.handleResizeMove);
                document.removeEventListener('mouseup', this.handleResizeEnd);
                document.removeEventListener('touchmove', this.handleResizeMove);
                document.removeEventListener('touchend', this.handleResizeEnd);
                document.removeEventListener('touchcancel', this.handleResizeEnd);
            }

            // Remove visual feedback
            this.window.classList.remove('st-window--resizing');
            document.body.style.userSelect = '';

            // Reset resize state
            this.isResizing = false;
            this.resizeDirection = null;
            this.pointerId = undefined;
        };

        /**
         * Setup keyboard shortcuts
         */
        setupKeyboard() {
            this.window.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.window.classList.contains('st-window--modal')) {
                    this.close();
                }
            });

            // Focus trap for modals
            if (this.window.classList.contains('st-window--modal')) {
                this.setupFocusTrap();
            }
        }

        /**
         * Setup focus trap for modals
         */
        setupFocusTrap() {
            this.window.addEventListener('keydown', (e) => {
                if (e.key !== 'Tab') return;

                const focusableElements = this.window.querySelectorAll(
                    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                );
                if (focusableElements.length === 0) return;

                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (e.shiftKey) {
                    if (document.activeElement === firstElement) {
                        e.preventDefault();
                        lastElement.focus();
                    }
                } else {
                    if (document.activeElement === lastElement) {
                        e.preventDefault();
                        firstElement.focus();
                    }
                }
            });
        }

        /**
         * Bring window to front
         */
        bringToFront() {
            if (window.smashOS && window.smashOS.windowManager) {
                window.smashOS.windowManager.bringToFront(this.windowId);
            }
        }
    }

    // Export to global scope
    window.WindowShell = WindowShell;
}

// Auto-initialize window shells (only once)
if (!window.__windowShellInitialized) {
    const initWindowShells = () => {
        const windows = document.querySelectorAll('.st-window:not([data-shell-initialized])');
        windows.forEach(windowEl => {
            if (!windowEl.windowShell) {
                windowEl.windowShell = new WindowShell(windowEl);
                windowEl.setAttribute('data-shell-initialized', 'true');
            }
        });
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initWindowShells);
    } else {
        initWindowShells();
    }

    // Also initialize dynamically added windows
    const observer = new MutationObserver(initWindowShells);
    observer.observe(document.body, { childList: true, subtree: true });

    window.__windowShellInitialized = true;
}

// Export for use in other modules
if (typeof window !== 'undefined') {
    window.WindowShell = WindowShell;
}

